import React, { useContext, useState } from 'react';

import checkCircleTokens from '@amzn/meridian-tokens/base/icon/check-circle';
import copyTokens from '@amzn/meridian-tokens/base/icon/copy';
import Alert from '@amzn/meridian/alert';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import 'src/components/ReleaseNotes/github-markdown.css';
import { AppContext } from 'src/contexts/appContext';
import { formatDateFriendly } from 'src/util/dateTimeUtil';

import { AIChatArtifact } from '../AIChatArtifact';
import { JsonDisplay } from '../AIChatArtifact/JsonDisplay';
import { Message } from '../types';
import { getArtifacts, isErrorMessage, timestampToNumber } from '../utils';
import { isToolMessage, isUserMessage } from '../utils';
import './MessageItem.scss';

interface MessageItemProps {
    message: Message;
    conversationId?: string;
    compact?: boolean;
}

export const MessageItem: React.FC<MessageItemProps> = ({ message, conversationId, compact = false }) => {
    const isUser = isUserMessage(message);
    const isTool = isToolMessage(message);
    const isError = isErrorMessage(message);
    const showDataSourceButton = isTool && conversationId;
    const { setIsRightSideBarOpen, setRightSideBarContent, setTopAlertMsg } = useContext(AppContext);
    const [copied, setCopied] = useState(false);

    const handleShowInput = () => {
        setIsRightSideBarOpen(true);
        const inputString = message.toolUsage?.toolInput;
        setRightSideBarContent({
            heading: 'Tool Input',
            mainBody: <JsonDisplay data={inputString ?? ''} />
        });
    };

    const handleCopy = async () => {
        const content = message.messageContent || '';
        await navigator.clipboard.writeText(content);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleShowArtifacts = async () => {
        if (!conversationId) return;

        // Show loading state immediately
        setIsRightSideBarOpen(true);
        setRightSideBarContent({
            heading: 'Artifacts',
            mainBody: (
                <AIChatArtifact
                    artifacts={[]}
                    isLoading={true}
                    conversationId={conversationId}
                    timestamp={message.timestamp}
                />
            )
        });

        try {
            // Fetch artifacts - convert to string to preserve precision
            const artifacts = await getArtifacts(conversationId, message.timestamp.toString());

            // Update with loaded artifacts
            setRightSideBarContent({
                heading: 'Artifacts',
                mainBody: (
                    <AIChatArtifact
                        artifacts={artifacts}
                        isLoading={false}
                        conversationId={conversationId}
                        timestamp={message.timestamp}
                    />
                )
            });
        } catch (error) {
            setRightSideBarContent({
                heading: 'Artifacts',
                mainBody: (
                    <Column>
                        <Text>Failed to load artifacts</Text>
                        <Text>{error instanceof Error ? error.message : 'Unknown error'}</Text>
                    </Column>
                )
            });
            setTopAlertMsg(error);
        }
    };

    const getFriendlyDateTime = (timestamp: string) => {
        return formatDateFriendly(new Date(timestampToNumber(timestamp) / 1000000));
    };

    const renderCompactContent = () => {
        if (message.messageType === 'tool' && message.toolUsage) {
            return (
                <Row alignmentHorizontal="justify" alignmentVertical="center">
                    <Text>Calling tool {message.toolUsage.toolName.split('___')[1]}</Text>
                    <Row spacing="100">
                        <Button type="link" size="small" onClick={handleShowInput}>
                            View Input
                        </Button>
                        <Button type="link" size="small" onClick={handleShowArtifacts}>
                            View Artifacts
                        </Button>
                    </Row>
                </Row>
            );
        }

        return (
            <Row maxWidth="60%">
                <Text>{message.messageContent}</Text>
            </Row>
        );
    };

    const renderMessageContent = () => {
        if (message.messageType === 'system') {
            return (
                <Column spacing="100 none">
                    <ReactMarkdown className="markdown-body" remarkPlugins={[remarkGfm]}>
                        {message.messageContent ?? ''}
                    </ReactMarkdown>
                    <Row alignmentHorizontal="justify" alignmentVertical="center">
                        <Text type="b100" color="secondary">
                            {getFriendlyDateTime(message.timestamp)}
                        </Text>
                        <Button type="icon" onClick={handleCopy} className="copy-button">
                            <Icon tokens={copied ? checkCircleTokens : copyTokens} className="copy-icon" />
                        </Button>
                    </Row>
                </Column>
            );
        }

        if (message.messageContent) {
            return (
                <Column spacing="100">
                    <p style={{ whiteSpace: 'pre-wrap' }}>{message.messageContent}</p>
                    <Row alignmentHorizontal="justify" alignmentVertical="center">
                        <Text type="b100" color="secondary">
                            {getFriendlyDateTime(message.timestamp)}
                        </Text>
                        <Button type="icon" onClick={handleCopy} className="copy-button">
                            <Icon tokens={copied ? checkCircleTokens : copyTokens} className="copy-icon" />
                        </Button>
                    </Row>
                </Column>
            );
        }

        if (message.toolUsage) {
            return (
                <Column spacing="100">
                    <Row>
                        <Text>
                            Calling tool <b>{message.toolUsage.toolName.split('___')[1]}</b>
                        </Text>
                    </Row>
                    {showDataSourceButton && (
                        <Row alignmentHorizontal="justify">
                            <Text type="b100" color="secondary">
                                {getFriendlyDateTime(message.timestamp)}
                            </Text>
                            <Button type="link" size="small" onClick={handleShowInput}>
                                View Input
                            </Button>
                            <Button type="link" size="small" onClick={handleShowArtifacts}>
                                View Artifacts
                            </Button>
                        </Row>
                    )}
                </Column>
            );
        }

        return (
            <Column>
                <Text>No content available</Text>
            </Column>
        );
    };

    // Compact mode for messages - simple row layout
    if (compact) {
        return renderCompactContent();
    }

    // Default mode - full message layout with boxes
    return (
        <Row alignmentHorizontal={isUser ? 'end' : 'start'}>
            <Column className="message-wrapper" maxWidth="60%">
                {getMessageBubble(isUser, isError, renderMessageContent())}
            </Column>
        </Row>
    );
};

function getMessageBubble(isUser: boolean, isError: boolean, content: JSX.Element) {
    if (isError) {
        return (
            <Alert type={'error'}>
                <Column spacing="100 none">{content}</Column>
            </Alert>
        );
    } else {
        return (
            <Box type="outline" spacingInset="400" className={isUser ? 'user-message' : 'agent-message'}>
                <Column spacing="100 none">{content}</Column>
            </Box>
        );
    }
}
